import { useState } from "react";
import { Trophy, Crown, Medal, Flame, ChevronLeft, ChevronRight } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { inr, numIN } from "@/lib/format";

const PAGE_SIZE = 50;

function CoachRow({ c, rank, myId, showCohortFilter }) {
  return (
    <TableRow data-testid={`leaderboard-row-${c.id}`}
              className={c.id === myId ? "bg-emerald-50" : ""}>
      <TableCell className="font-mono-num font-bold">{rank}</TableCell>
      <TableCell>
        <div className="font-medium">{c.name}{c.id === myId ? <Badge className="ml-2 bg-emerald-600 text-white">You</Badge> : null}</div>
      </TableCell>
      {showCohortFilter && (
        <TableCell className="hidden md:table-cell">{c.cohort ? <Badge variant="secondary">{c.cohort}</Badge> : "—"}</TableCell>
      )}
      <TableCell className="text-right font-mono-num font-bold text-emerald-700">{numIN(c.points)}</TableCell>
      <TableCell className="text-center">
        <span className={`inline-flex items-center gap-1 font-mono-num ${c.streak > 0 ? "text-red-500" : "text-muted-foreground"}`}>
          <Flame className={`h-4 w-4 ${c.streak >= 7 ? "animate-pulse" : ""}`} />{c.streak}
        </span>
      </TableCell>
      <TableCell>
        <div className="flex items-center gap-2">
          <Progress value={c.progress_pct} className="h-2" />
          <span className="font-mono-num text-xs w-9 text-right">{c.progress_pct}%</span>
        </div>
      </TableCell>
      <TableCell className="text-right font-mono-num text-emerald-700 hidden md:table-cell">{inr(c.total_money)}</TableCell>
      <TableCell className="text-right font-mono-num hidden md:table-cell">{numIN(c.total_clients)}</TableCell>
      <TableCell className="text-center hidden md:table-cell">
        <span className="inline-flex items-center gap-1 text-amber-600 font-mono-num">
          <Trophy className="h-3.5 w-3.5" />{c.badges_earned}
        </span>
      </TableCell>
    </TableRow>
  );
}

export default function Leaderboard({ rows = [], myId = null, myRow = null, cohorts = [], cohort = "all", onCohort, showCohortFilter = false }) {
  const [page, setPage] = useState(0);
  const top3 = rows.slice(0, 3);
  const podiumOrder = [1, 0, 2];
  const pageCount = Math.max(1, Math.ceil(rows.length / PAGE_SIZE));
  const safePage = Math.min(page, pageCount - 1);
  const pageRows = rows.slice(safePage * PAGE_SIZE, safePage * PAGE_SIZE + PAGE_SIZE);

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-widest text-emerald-700">
          <Trophy className="h-4 w-4" /> Leaderboard · ranked by points
        </div>
        {showCohortFilter && (
          <Select value={cohort} onValueChange={(v) => { setPage(0); onCohort(v); }}>
            <SelectTrigger className="w-[180px]" data-testid="leaderboard-cohort-filter">
              <SelectValue placeholder="All cohorts" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All cohorts</SelectItem>
              {cohorts.map((c) => (
                <SelectItem key={c} value={c}>{c}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        )}
      </div>

      {top3.length > 0 && (
        <div className="rounded-2xl border border-border bg-gradient-to-b from-emerald-50 to-card p-6 sm:p-8 shadow-sm" data-testid="scoreboard-top3-podium">
          <div className="grid grid-cols-3 gap-4 items-end max-w-3xl mx-auto">
            {podiumOrder.map((idx) => {
              const c = top3[idx];
              if (!c) return <div key={idx} />;
              const rank = idx + 1;
              const heights = { 1: "h-36", 2: "h-28", 3: "h-24" };
              const colors = {
                1: "from-amber-300 to-amber-500 text-amber-950",
                2: "from-slate-200 to-slate-400 text-slate-800",
                3: "from-orange-200 to-orange-400 text-orange-950",
              };
              const Icon = rank === 1 ? Crown : Medal;
              return (
                <div key={c.id} className="flex flex-col items-center" data-testid={`podium-rank-${rank}`}>
                  <div className={`h-12 w-12 rounded-full bg-white border-2 flex items-center justify-center font-display font-bold mb-2 shadow ${c.id === myId ? "border-emerald-600 text-emerald-700 ring-2 ring-emerald-300" : "border-emerald-400 text-emerald-700"}`}>
                    {(c.name || "?").slice(0, 1).toUpperCase()}
                  </div>
                  <div className="font-medium text-sm text-center truncate max-w-[130px]">{c.name}{c.id === myId ? " (you)" : ""}</div>
                  <div className="font-mono-num text-emerald-700 font-bold text-sm">{numIN(c.points)} pts</div>
                  <div className={`w-full ${heights[rank]} rounded-t-xl bg-gradient-to-b ${colors[rank]} mt-2 flex flex-col items-center justify-start pt-3 shadow-sm`}>
                    <Icon className="h-6 w-6" />
                    <div className="font-display text-2xl font-extrabold mt-1">#{rank}</div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      <div className="rounded-2xl border border-border bg-card shadow-sm overflow-x-auto">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="w-12">#</TableHead>
              <TableHead>Coach</TableHead>
              {showCohortFilter && <TableHead className="hidden md:table-cell">Cohort</TableHead>}
              <TableHead className="text-right">Points</TableHead>
              <TableHead className="text-center">Streak</TableHead>
              <TableHead className="w-24 md:w-[160px]">Progress</TableHead>
              <TableHead className="text-right hidden md:table-cell">Earned</TableHead>
              <TableHead className="text-right hidden md:table-cell">Clients</TableHead>
              <TableHead className="text-center hidden md:table-cell">Badges</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {pageRows.map((c, i) => (
              <CoachRow key={c.id} c={c} rank={safePage * PAGE_SIZE + i + 1} myId={myId} showCohortFilter={showCohortFilter} />
            ))}
          </TableBody>
        </Table>
      </div>

      {pageCount > 1 && (
        <div className="flex items-center justify-between gap-3 text-sm text-muted-foreground" data-testid="leaderboard-pager">
          <span data-testid="leaderboard-pager-info">Showing {safePage * PAGE_SIZE + 1}–{Math.min((safePage + 1) * PAGE_SIZE, rows.length)} of {rows.length}</span>
          <div className="flex gap-2">
            <Button variant="outline" size="sm" disabled={safePage === 0} onClick={() => setPage(safePage - 1)} data-testid="leaderboard-page-prev">
              <ChevronLeft className="h-4 w-4 mr-1" /> Previous
            </Button>
            <Button variant="outline" size="sm" disabled={safePage >= pageCount - 1} onClick={() => setPage(safePage + 1)} data-testid="leaderboard-page-next">
              Next <ChevronRight className="h-4 w-4 ml-1" />
            </Button>
          </div>
        </div>
      )}

      {myRow && null}
    </div>
  );
}
